// ON-333: the share landing page renders in the language the link carries.
//
// The app appends ?lang= when it builds a share URL (buildShareUrl in the app's
// src/utils/links.ts), so the page a recipient opens matches the language the
// sharer was using. Accept-Language cannot drive this — the crawler that
// produces the link preview sends its own — so the param is the signal for both
// the meta tags and the page body, and they must agree.
//
// Deliberately dependency-free: this is two locales over one page, and pulling
// in an i18n runtime for it would cost more than it saves.
//
// The meta-tag half of these strings is mirrored in functions/_shared/og.ts.
// The two live apart because functions/ and src/ are separate TypeScript
// projects bundled by different toolchains; sharing a module across that
// boundary risks the deploy for the sake of a handful of strings.

import type { ApplyMode } from './registrationMode'
import type { SignupErrorKey } from './signupForm'

/**
 * ON-407: web RSVP + account creation. Signup labels and validation copy
 * mirror the app's (stazon-app src/i18n/locales: signUp.*, login.*,
 * validation.*) so the two read the same.
 */
export interface RsvpStrings {
  rsvpCta: string
  openInApp: string
  close: string
  createTab: string
  loginTab: string
  createTitle: string
  createSubtitle: string
  loginTitle: string
  continueWithGoogle: string
  continueWithApple: string
  or: string
  fullName: string
  email: string
  password: string
  passwordPlaceholder: string
  confirmPassword: string
  iAccept: string
  termsAndConditions: string
  privacyPolicy: string
  createAccount: string
  creatingAccount: string
  signIn: string
  signingIn: string
  forgotPassword: string
  sendResetLink: string
  resetSent: (email: string) => string
  backToLogin: string
  validation: Record<SignupErrorKey, string>
  invalidCredentials: string
  emailNotConfirmed: string
  resendConfirmation: string
  resent: string
  alreadyRegistered: string
  rateLimited: string
  network: string
  genericError: string
  checkEmailTitle: string
  checkEmailBody: (email: string) => string
  checkEmailHint: string
  emailConfirmedLogin: string
  linkExpired: string
  termsTitle: string
  termsBody: string
  continue: string
  confirmTitle: string
  signedInAs: (email: string) => string
  confirmButton: string
  confirming: string
  signOut: string
  registeredTitle: string
  registeredBody: string
  pickupCode: (code: string) => string
  claimNote: string
  waitlistedTitle: string
  waitlistedBody: string
  alreadyRegisteredTitle: string
  alreadyWaitlistedTitle: string
  closedTitle: string
  closedBody: string
  needsAppTitle: string
  needsAppBody: string
  rsvpInApp: string
  notFoundBody: string
  errorTitle: string
  tryAgain: string
  done: string
}

export type Lang = 'en' | 'zh-TW'

const LANGS: Lang[] = ['en', 'zh-TW']

/** True only for an exact supported locale code — for values read back from storage. */
export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (LANGS as string[]).includes(v)
}

function match(tag: string | null | undefined): Lang | null {
  if (!tag) return null
  const lower = tag.toLowerCase()
  const exact = LANGS.find((l) => l.toLowerCase() === lower)
  if (exact) return exact
  // Traditional Chinese arrives in several shapes from a browser's language
  // list ('zh-Hant', 'zh-Hant-TW', 'zh-HK'). Simplified is left to fall through
  // to English rather than being served Traditional.
  if (lower.startsWith('zh-hant') || lower === 'zh-tw' || lower === 'zh-hk') return 'zh-TW'
  return null
}

/**
 * Resolve the page language: the ?lang= param the app put on the link, then the
 * browser's own preference, then English.
 *
 * The param wins over the browser because it reflects the sharer's choice of
 * language for this particular link, which is a better signal about the
 * recipient than the device default of whoever happens to open it.
 */
export function resolveLang(search: string, navigatorLanguages?: readonly string[]): Lang {
  const fromParam = match(new URLSearchParams(search).get('lang'))
  if (fromParam) return fromParam

  for (const tag of navigatorLanguages ?? []) {
    const fromNavigator = match(tag)
    if (fromNavigator) return fromNavigator
  }

  return 'en'
}

interface Strings {
  eventTypes: Record<string, string>
  difficulty: Record<string, string>
  /**
   * Description used when the organizer wrote none. Must stay in step with
   * OG_STRINGS.hostedBy in functions/_shared/og.ts — the Pages Function's tags
   * are what a crawler scrapes, these are what a browser ends up with, and the
   * two disagreeing is a bug nobody would notice until it mattered.
   */
  hostedBy: (type: string, club: string) => string
  /**
   * ON-464: appended to the hostedBy fallback for selection / lottery events.
   * Mirrored in OG_STRINGS.applySuffix in functions/_shared/og.ts, for the
   * same reason as hostedBy.
   */
  applySuffix: Record<ApplyMode, string>
  /** Used instead of applySuffix once applications have closed. Mirrored in OG_STRINGS.closedSuffix. */
  closedSuffix: Record<ApplyMode, string>
  eventBadge: string
  /** Byline label beside the club name; sits after it in English, before it in Chinese. */
  hostedByLabel: string
  when: string
  where: string
  distance: string
  pace: string
  level: string
  spotsLeft: string
  theRun: string
  notFoundTitle: string
  notFoundBody: string
  loadFailed: string
  /** ON-390: photo-count teaser for logged-out visitors; n is always > 0 when rendered. */
  photosTeaser: (n: number) => string
  joinEventCta: string
  /** ON-464: short badge for a selection / lottery event. */
  modeBadge: Record<ApplyMode, string>
  /**
   * ON-464: replaces "N spots left" — the total the host will fill and how.
   * n is null when the event is uncapped.
   */
  spotsBy: Record<ApplyMode, (n: number | null) => string>
  /** ON-464: label beside the live application count. */
  applied: string
  /** ON-464: application deadline; `date` is already formatted. */
  applyBy: (date: string) => string
  /** ON-464: the host has closed the pool, or the deadline has passed. */
  selectionClosed: string
  /** ON-464: CTA for a selection / lottery event still taking applications. */
  applyCta: string
  /** ON-464: CTA once applications have closed — nothing left to apply to. */
  viewEventCta: string
  /** ON-464: smart banner subline on a selection / lottery event. */
  applyBannerSubtitle: string
  /** ON-407: web RSVP sheet. */
  rsvp: RsvpStrings
}

export const STRINGS: Record<Lang, Strings> = {
  en: {
    eventTypes: {
      social_street: 'Social / Street',
      track: 'Track',
      trail: 'Trail',
      event: 'Event',
      race: 'Race',
      // Legacy values, still on old records.
      group_run: 'Group Run',
      training: 'Training',
      social: 'Social',
    },
    difficulty: {
      Beginner: 'Beginner',
      Intermediate: 'Intermediate',
      Advanced: 'Advanced',
    },
    hostedBy: (type, club) => `${type} hosted by ${club}`,
    applySuffix: {
      lottery: ' · Spots chosen by lottery — apply in the Stazon app',
      selection: ' · Runners selected by the host — apply in the Stazon app',
    },
    closedSuffix: {
      lottery: ' · Spots chosen by lottery — applications closed',
      selection: ' · Runners selected by the host — applications closed',
    },
    eventBadge: 'EVENT',
    hostedByLabel: 'Hosted by',
    when: 'When',
    where: 'Where',
    distance: 'Distance',
    pace: 'Pace / km',
    level: 'Level',
    spotsLeft: 'Spots left',
    theRun: 'The run',
    notFoundTitle: 'Event not found',
    notFoundBody: 'This event is not available or may have been removed.',
    loadFailed: 'Could not load event',
    photosTeaser: (n) => `${n} ${n === 1 ? 'photo' : 'photos'} from this run — sign up to see ${n === 1 ? 'it' : 'them'}`,
    joinEventCta: 'Join this run in the app',
    modeBadge: {
      lottery: 'Lottery',
      selection: 'Selection',
    },
    spotsBy: {
      lottery: (n) => (n == null ? 'Spots chosen by lottery' : `${n} ${n === 1 ? 'spot' : 'spots'} · chosen by lottery`),
      selection: (n) => (n == null ? 'Runners selected by the host' : `${n} ${n === 1 ? 'spot' : 'spots'} · selected by the host`),
    },
    applied: 'Applied',
    applyBy: (date) => `Apply by ${date}`,
    selectionClosed: 'Selection closed',
    applyCta: 'Apply in the app',
    viewEventCta: 'See this run in the app',
    applyBannerSubtitle: 'Apply in the app · Free',
    rsvp: {
      rsvpCta: 'RSVP to this run',
      openInApp: 'Open in the app',
      close: 'Close',
      createTab: 'Create account',
      loginTab: 'Log in',
      createTitle: 'RSVP with a free Stazon account',
      createSubtitle: 'No download needed. The same account works in the app.',
      loginTitle: 'Log in to RSVP',
      continueWithGoogle: 'Continue with Google',
      continueWithApple: 'Continue with Apple',
      or: 'or',
      fullName: 'Full Name',
      email: 'Email',
      password: 'Password',
      passwordPlaceholder: 'Password (min. 8 characters)',
      confirmPassword: 'Confirm Password',
      iAccept: 'I accept the',
      termsAndConditions: 'Terms & Conditions',
      privacyPolicy: 'Privacy Policy',
      createAccount: 'Create Account',
      creatingAccount: 'Creating Account...',
      signIn: 'Log in',
      signingIn: 'Signing in...',
      forgotPassword: 'Forgot Password?',
      sendResetLink: 'Send reset link',
      resetSent: (email) => `If ${email} has an account, a password reset link is on its way. Check your inbox and spam folder.`,
      backToLogin: 'Back to log in',
      validation: {
        fullNameRequired: 'Full name is required',
        emailRequired: 'Email is required',
        invalidEmail: 'Please enter a valid email address',
        passwordRequirements: 'Password must be at least 8 characters with a letter and number',
        passwordsNoMatch: 'Passwords do not match',
        acceptTerms: 'You must accept the Terms & Conditions',
        acceptPrivacy: 'You must accept the Privacy Policy',
      },
      invalidCredentials: 'Invalid email or password. Please try again.',
      emailNotConfirmed: 'Please confirm your email first — check your inbox for the link.',
      resendConfirmation: 'Resend confirmation email',
      resent: 'Confirmation email sent!',
      alreadyRegistered: 'This email already has a Stazon account. Log in to RSVP.',
      rateLimited: 'Too many attempts. Please wait a moment and try again.',
      network: 'Network problem. Check your connection and try again.',
      genericError: 'Something went wrong. Please try again.',
      checkEmailTitle: 'Check your email',
      checkEmailBody: (email) => `We've sent a confirmation link to ${email}.`,
      checkEmailHint: 'Open it on this device to finish your RSVP. Didn’t get it? Check your spam folder or resend.',
      emailConfirmedLogin: 'Email confirmed — log in to finish your RSVP.',
      linkExpired: 'That link has expired or was already used. Log in to get a new one.',
      termsTitle: 'One more step',
      termsBody: 'Accept the Terms & Conditions and Privacy Policy to continue.',
      continue: 'Continue',
      confirmTitle: 'Confirm your RSVP',
      signedInAs: (email) => `Signed in as ${email}`,
      confirmButton: 'Confirm RSVP',
      confirming: 'Confirming...',
      signOut: 'Not you? Sign out',
      registeredTitle: "You're in!",
      registeredBody: 'See you at the start line. Get the app for reminders, chat and photos.',
      pickupCode: (code) => `Pickup code: ${code}`,
      claimNote: 'Your sponsor pack uses the default picks — you can change them in the app.',
      waitlistedTitle: "You're on the waitlist",
      waitlistedBody: "This run is full. We'll let you know if a spot opens up.",
      alreadyRegisteredTitle: "You're already registered",
      alreadyWaitlistedTitle: "You're already on the waitlist",
      closedTitle: 'Registration has closed',
      closedBody: 'This run is no longer taking RSVPs.',
      needsAppTitle: 'RSVP in the app',
      needsAppBody: 'This run needs a few more details that only the Stazon app can collect.',
      rsvpInApp: 'RSVP in the app',
      notFoundBody: 'This event is not available or may have been removed.',
      errorTitle: 'Could not RSVP',
      tryAgain: 'Try again',
      done: 'Done',
    },
  },
  'zh-TW': {
    eventTypes: {
      social_street: '社交／街跑',
      track: '田徑場',
      trail: '越野',
      event: '活動',
      race: '比賽',
      // Legacy values, still on old records.
      group_run: '團跑',
      training: '訓練',
      social: '社交活動',
    },
    difficulty: {
      Beginner: '初級',
      Intermediate: '中級',
      Advanced: '進階',
    },
    hostedBy: (type, club) => `${club} 主辦的${type}`,
    applySuffix: {
      lottery: '・名額以抽籤決定，請在 Stazon App 中申請',
      selection: '・由主辦方挑選參加者，請在 Stazon App 中申請',
    },
    closedSuffix: {
      lottery: '・名額以抽籤決定，申請已截止',
      selection: '・由主辦方挑選參加者，申請已截止',
    },
    eventBadge: '活動',
    hostedByLabel: '主辦',
    when: '時間',
    where: '地點',
    distance: '距離',
    pace: '配速 / 公里',
    level: '程度',
    spotsLeft: '剩餘名額',
    theRun: '關於這次跑步',
    notFoundTitle: '找不到活動',
    notFoundBody: '此活動不存在或已被移除。',
    loadFailed: '無法載入活動',
    photosTeaser: (n) => `這次跑步有 ${n} 張照片 — 註冊後即可查看`,
    joinEventCta: '在 App 中加入這次跑步',
    modeBadge: {
      lottery: '抽籤',
      selection: '甄選',
    },
    spotsBy: {
      lottery: (n) => (n == null ? '名額以抽籤決定' : `${n} 個名額 · 抽籤決定`),
      selection: (n) => (n == null ? '由主辦方挑選參加者' : `${n} 個名額 · 由主辦方挑選`),
    },
    applied: '已申請',
    applyBy: (date) => `${date} 前申請`,
    selectionClosed: '甄選已截止',
    applyCta: '在 App 中申請',
    viewEventCta: '在 App 中查看這次跑步',
    applyBannerSubtitle: '在 App 中申請 · 免費',
    rsvp: {
      rsvpCta: '報名參加這次跑步',
      openInApp: '在 App 中開啟',
      close: '關閉',
      createTab: '建立帳號',
      loginTab: '登入',
      createTitle: '用免費 Stazon 帳號報名',
      createSubtitle: '無需下載，同一帳號亦可在 App 中使用。',
      loginTitle: '登入以報名',
      continueWithGoogle: '使用 Google 繼續',
      continueWithApple: '使用 Apple 繼續',
      or: '或',
      fullName: '全名',
      email: '電子郵件',
      password: '密碼',
      passwordPlaceholder: '密碼（至少 8 個字元）',
      confirmPassword: '確認密碼',
      iAccept: '我同意',
      termsAndConditions: '服務條款',
      privacyPolicy: '隱私權政策',
      createAccount: '建立帳號',
      creatingAccount: '建立帳號中...',
      signIn: '登入',
      signingIn: '登入中...',
      forgotPassword: '忘記密碼？',
      sendResetLink: '發送重設連結',
      resetSent: (email) => `如果 ${email} 已註冊帳號，你將會收到密碼重設連結。請檢查你的收件匣和垃圾郵件資料夾。`,
      backToLogin: '返回登入',
      validation: {
        fullNameRequired: '請輸入全名',
        emailRequired: '請輸入電子郵件',
        invalidEmail: '請輸入有效的電子郵件地址',
        passwordRequirements: '密碼必須至少 8 個字元，並包含字母和數字',
        passwordsNoMatch: '密碼不相符',
        acceptTerms: '你必須同意服務條款',
        acceptPrivacy: '你必須同意隱私權政策',
      },
      invalidCredentials: '電子郵件或密碼錯誤，請重新輸入。',
      emailNotConfirmed: '請先確認你的電子郵件 — 請在收件匣中查看確認連結。',
      resendConfirmation: '重新發送確認信',
      resent: '確認信已發送！',
      alreadyRegistered: '此電子郵件已有 Stazon 帳號，請登入以報名。',
      rateLimited: '嘗試次數過多，請稍後再試。',
      network: '網絡問題，請檢查連線後再試。',
      genericError: '發生錯誤，請重試。',
      checkEmailTitle: '檢查你的信箱',
      checkEmailBody: (email) => `我們已發送確認連結至 ${email}。`,
      checkEmailHint: '請在此裝置上開啟連結以完成報名。沒收到？請檢查垃圾郵件資料夾或重新發送。',
      emailConfirmedLogin: '電子郵件已確認 — 請登入以完成報名。',
      linkExpired: '連結已過期或已使用。請登入以重新申請。',
      termsTitle: '還差一步',
      termsBody: '請同意服務條款及隱私權政策以繼續。',
      continue: '繼續',
      confirmTitle: '確認報名',
      signedInAs: (email) => `已登入：${email}`,
      confirmButton: '確認報名',
      confirming: '確認中...',
      signOut: '不是你？登出',
      registeredTitle: '報名成功！',
      registeredBody: '起跑線見！下載 App 以接收提醒、聊天及查看照片。',
      pickupCode: (code) => `領取編號：${code}`,
      claimNote: '你的贊助禮品已套用預設選項，可在 App 中更改。',
      waitlistedTitle: '你已加入候補名單',
      waitlistedBody: '這次跑步已額滿。如有名額空出，我們會通知你。',
      alreadyRegisteredTitle: '你已報名這次跑步',
      alreadyWaitlistedTitle: '你已在候補名單中',
      closedTitle: '報名已截止',
      closedBody: '這次跑步已不再接受報名。',
      needsAppTitle: '在 App 中報名',
      needsAppBody: '這次跑步需要填寫更多資料，只能在 Stazon App 中完成。',
      rsvpInApp: '在 App 中報名',
      notFoundBody: '此活動不存在或已被移除。',
      errorTitle: '無法報名',
      tryAgain: '重試',
      done: '完成',
    },
  },
}

/**
 * The description used when the organizer wrote none. Keep in step with
 * fallbackDescription in functions/_shared/og.ts, which builds the crawler's
 * copy of the same tag.
 */
export function fallbackDescription(
  s: Strings,
  eventType: string,
  club: string,
  mode: ApplyMode | null,
  closed = false,
): string {
  const base = s.hostedBy(s.eventTypes[eventType] ?? eventType, club)
  if (!mode) return base
  return base + (closed ? s.closedSuffix[mode] : s.applySuffix[mode])
}

/** BCP-47 tag for Intl formatting. */
export const intlLocale: Record<Lang, string> = {
  en: 'en',
  'zh-TW': 'zh-TW',
}
